'use client'

import React from 'react'
import Link from 'next/link'
import BlogPostLayout from '@/app/components/BlogPostLayout'
import { BlogTLDR, BlogKeyTakeaways, BlogFAQ, BlogSources, BlogLastUpdated, BlogComparisonTable, BlogAppCTA } from '@/app/components/blog/BlogAeo'
import RiskQuizInlineNudge from '@/app/components/blog/RiskQuizInlineNudge'
import InlineGuessReveal from '@/app/components/blog/InlineGuessReveal'
import KeyStats from '@/app/components/blog/KeyStats'

export default function PostContent() {
  return (
    <BlogPostLayout
      title="Drugstore Skincare Audited: 290 Formulas from CeraVe, Cetaphil and Neutrogena (2026)"
      author="MicroPlastics Research Desk"
      date="August 8, 2026"
      readTime="14 min read"
      heroImage="/blog/heroes/drugstore-skincare-microplastics-audited.webp"
      heroImageAlt="Assorted skincare bottles, jars, tubes and droppers arranged around a magnifying glass"
    >
      <BlogLastUpdated iso="2026-08-08" formatted="August 8, 2026" />

      <BlogTLDR>
        <strong>We pulled every ingredient list the three biggest drugstore
        skincare brands publish themselves — 290 formulas — and counted solid
        synthetic polymer particles. 47 contain one.</strong> The split is not
        what the shelf suggests.{' '}
        <strong>CeraVe came in at 4 of 71 formulas (6%)</strong> and{' '}
        <strong>Cetaphil at 5 of 79 (6%)</strong>, while{' '}
        <strong>Neutrogena hit 38 of 140 (27%)</strong>. The single biggest
        driver is one product line: <strong>Hydro Boost accounts for 15 of
        Neutrogena&apos;s 38 hits</strong>, which means the range marketed on
        water and hyaluronic acid is the most polymer-heavy in the brand. All
        three brands, though, are dramatically cleaner than the{' '}
        <Link href="/blog/microplastics-in-makeup-brands-audited" className="text-forest underline">prestige makeup brands we audited in July</Link>.
        The dermatologist aisle is genuinely better. It is just not uniform.
      </BlogTLDR>

      <BlogKeyTakeaways
        items={[
          <><strong>290 formulas audited</strong> from the brands&apos; own published INCI lists. No third-party databases.</>,
          <><strong>CeraVe 4/71 (6%)</strong> — the cleanest of the three, and the cleanest big brand we have audited in any category.</>,
          <><strong>Cetaphil 5/79 (6%)</strong> — statistically level with CeraVe.</>,
          <><strong>Neutrogena 38/140 (27%)</strong> — four and a half times the rate of the other two.</>,
          <><strong>Hydro Boost is 15 of those 38.</strong> The &ldquo;water gel&rdquo; line is the polymer-heaviest thing Neutrogena sells.</>,
          <>Half of Neutrogena&apos;s hits are <strong>makeup, not skincare</strong> — its catalogue includes foundation and mascara, which inflates the headline rate.</>,
          <>We excluded <strong>polyquaternium</strong> from the counts. It is a synthetic polymer but a water-soluble one, so it is not a solid particle. Including it would have added 22 products.</>,
          <>None of this is banned. Cosmetic microplastics stay legal in the EU until <strong>October 2035</strong>.</>,
        ]}
      />

      <KeyStats
        heading="The audit, in numbers"
        stats={[
          { value: '290', label: 'formulas with published INCI', context: 'scraped from cerave.com, cetaphil.com and neutrogena.com directly', source: 'MicroPlastics Research Desk audit, August 2026' },
          { value: '47', label: 'contain a solid polymer particle', context: '16% of the total across all three brands', source: 'MicroPlastics Research Desk audit, August 2026' },
          { value: '6% vs 27%', label: 'CeraVe/Cetaphil vs Neutrogena', context: 'the gap between the two gentle-positioned brands and the third', source: 'MicroPlastics Research Desk audit, August 2026' },
          { value: '15', label: 'Hydro Boost products with a solid polymer', context: 'a single line accounting for nearly 40% of Neutrogena’s hits', source: 'MicroPlastics Research Desk audit, August 2026' },
        ]}
      />

      <h2>How we did this, and why it is different</h2>
      <p>
        Ingredient databases are the usual shortcut for audits like this, and we
        do not use them. They are user-uploaded, frequently stale, and often
        describe a formula that was reformulated two years ago. Instead we{' '}
        <strong>walked each brand&apos;s own product sitemap, fetched every
        product page, and extracted the INCI list the brand itself
        publishes</strong> in August 2026.
      </p>
      <p>
        Then we split polymers into two groups, which is the step most coverage
        skips:
      </p>
      <ul>
        <li>
          <strong>Solid polymer particles</strong> — nylon-12, polyethylene,
          PMMA, PET, acrylates copolymer, silica dimethyl silylate, the
          crosspolymers. These are discrete solid particles, and they are what
          the EU restriction on synthetic polymer microparticles actually
          targets.
        </li>
        <li>
          <strong>Liquid or soluble polymers</strong> — dimethicone,
          cyclopentasiloxane, the PEGs, polyquaternium. Real petrochemical
          polymers, but not solid particles, so not microplastics under that
          definition.
        </li>
      </ul>
      <p>
        That distinction changes the answer substantially. Counting
        polyquaternium as a microplastic would have added 22 products and made
        Neutrogena&apos;s cleansers and every baby wash in the Cetaphil range
        look contaminated. It is a conditioning polymer that dissolves in the
        bottle. We left it out and say so, because a number you cannot reproduce
        is not worth publishing.
      </p>

      <InlineGuessReveal
        source="drugstore_skincare_dirtiest_line"
        question="Which Neutrogena line contains the most products with solid polymer particles?"
        options={[
          { label: 'Rapid Wrinkle Repair' },
          { label: 'Hydro Boost', correct: true },
          { label: 'Deep Clean' },
          { label: 'Ultra Sheer sunscreens' },
        ]}
        answer="Hydro Boost"
        answerNote={
          <>
            Fifteen Hydro Boost products contain a solid synthetic polymer —
            nearly 40% of every hit in the brand. The line is sold on water,
            hyaluronic acid and lightness, and the gel texture that delivers that
            feel is built with acrylates copolymer and dimethicone crosspolymers.
            The marketing and the formula are pulling in opposite directions.
          </>
        }
        ctaText="Your bathroom shelf is not in this audit. Scan it and find out where it lands."
        ctaLabel="Scan a product"
      />

      <RiskQuizInlineNudge source="blog_inline_drugstore_skincare" />

      <h2>The results</h2>

      <BlogComparisonTable
        caption="Solid synthetic polymer particles across 290 published ingredient lists, brands' own sites, August 2026. Water-soluble polymers such as polyquaternium and dimethicone are excluded."
        columns={[
          { key: 'brand', label: 'Brand' },
          { key: 'audited', label: 'Formulas audited' },
          { key: 'hits', label: 'With solid polymer' },
          { key: 'rate', label: 'Rate' },
          { key: 'note', label: 'Where they cluster' },
        ]}
        rows={[
          { brand: 'CeraVe', audited: '71', hits: '4', rate: '6%', note: 'A cleansing balm, a body wash, the retinol serum and one SPF lotion. The core moisturisers are clean.' },
          { brand: 'Cetaphil', audited: '79', hits: '5', rate: '6%', note: 'Almost entirely the SPF moisturisers and one pimple patch. The classic cleanser and lotion are clean.' },
          { brand: 'Neutrogena', audited: '140', hits: '38', rate: '27%', note: 'Hydro Boost (15), plus makeup, sunscreens and the Body Clear range.' },
        ]}
      />

      <p>
        The headline gap needs one honest qualification.{' '}
        <strong>Neutrogena&apos;s catalogue is not a like-for-like
        comparison</strong> — it sells foundation, mascara, concealer and
        shampoo alongside skincare, and CeraVe and Cetaphil largely do not. So
        we split its 140 formulas by category, which is the fair way to read
        the 27%:
      </p>
      <ul>
        <li><strong>Skincare: 18 of 72 (25%)</strong></li>
        <li><strong>Makeup: 20 of 54 (37%)</strong> — in line with the ~30% we found across prestige makeup in July, so this is a colour-cosmetics norm rather than a Neutrogena problem</li>
        <li><strong>Haircare: 0 of 14</strong> — every shampoo and conditioner came back clean of solid particles</li>
      </ul>
      <p>
        The makeup range does inflate the headline, but not by as much as it
        first appears: <strong>Neutrogena&apos;s skincare alone still runs 25%,
        four times CeraVe&apos;s and Cetaphil&apos;s 6%.</strong> Splitting the
        catalogue does not rescue the comparison, it just locates it.
      </p>

      <h2>CeraVe: 4 of 71</h2>
      <p>
        The brand dermatologists recommend most is, on this measure, the
        cleanest large skincare brand we have audited. Its three ceramide
        moisturisers — the products most people actually buy — contain no solid
        polymer particles at all.
      </p>
      <BlogComparisonTable
        caption="Every CeraVe formula containing a solid synthetic polymer particle (cerave.com, August 2026)"
        columns={[
          { key: 'product', label: 'Product' },
          { key: 'polymer', label: 'Polymer' },
        ]}
        rows={[
          { product: 'Makeup Removing Cleanser Balm', polymer: 'Polyethylene' },
          { product: 'Soothing Body Wash for Very Dry Skin', polymer: 'Silica dimethyl silylate' },
          { product: 'Skin Renewing Retinol Serum', polymer: 'Vinyl dimethicone crosspolymer' },
          { product: 'Ultra-Light Moisturizing Lotion SPF 30', polymer: 'Methyl methacrylate crosspolymer' },
        ]}
      />
      <p>
        Full detail in{' '}
        <Link href="/blog/does-cerave-have-microplastics" className="text-forest underline">does CeraVe have microplastics</Link>.
      </p>

      <h2>Cetaphil: 5 of 79</h2>
      <p>
        Level with CeraVe, and clustered almost entirely in one place:{' '}
        <strong>the SPF moisturisers</strong>. Both the Daily Facial Moisturizer
        SPF 15 and the DermaControl SPF 30 use polymethyl methacrylate, and the
        Healthy Radiance day cream uses acrylates copolymer. That is a pattern,
        not a coincidence — sunscreen textures lean on polymer spheres for feel
        and finish.
      </p>
      <BlogComparisonTable
        caption="Every Cetaphil formula containing a solid synthetic polymer particle (cetaphil.com, August 2026)"
        columns={[
          { key: 'product', label: 'Product' },
          { key: 'polymer', label: 'Polymer' },
        ]}
        rows={[
          { product: 'Daily Facial Moisturizer SPF 15', polymer: 'Polymethyl methacrylate' },
          { product: 'DermaControl Oil Absorbing Moisturizer SPF 30', polymer: 'Polymethyl methacrylate' },
          { product: 'Healthy Radiance Whipped Day Cream SPF 30', polymer: 'Acrylates copolymer, styrene/acrylates copolymer' },
          { product: 'Hydrating Eye Gel-Cream', polymer: 'HDI/trimethylol hexyllactone crosspolymer' },
          { product: 'Fast Rescue Pimple Patch', polymer: 'Acrylates copolymer' },
        ]}
      />
      <p>
        Full detail in{' '}
        <Link href="/blog/does-cetaphil-have-microplastics" className="text-forest underline">does Cetaphil have microplastics</Link>.
      </p>

      <h2>Neutrogena, and the Hydro Boost problem</h2>
      <p>
        Hydro Boost is the line Neutrogena has built its modern identity on:
        water gel, hyaluronic acid, weightless hydration. It is also{' '}
        <strong>the most polymer-dense line in the brand</strong>, with 15
        products containing a solid synthetic polymer.
      </p>
      <p>
        This is not hypocrisy so much as chemistry. That distinctive bouncy,
        non-greasy gel texture is difficult to achieve with oils, and the
        industry standard route to it is{' '}
        <strong>acrylates copolymer and dimethicone crosspolymers</strong> —
        polymer networks that hold water in a gel and break under shear so the
        product feels light. The texture <em>is</em> the polymer. You cannot have
        the Hydro Boost feel without something doing that job.
      </p>
      <p>
        The affected products run right through the line: the Water Gel
        moisturiser, the Gel-Cream for Eyes, the niacinamide serum, the
        exfoliating cleanser, both sizes of the gel cleanser, the milk cleanser,
        the City Shield SPF and the lip sleeping mask.
      </p>
      <p>
        Put against Neutrogena&apos;s skincare alone, that is{' '}
        <strong>12 of its 18 skincare hits in one line</strong> — the rest is
        three sunscreens, two Body Clear products and a retinol serum. Full
        product-by-product breakdown in{' '}
        <Link href="/blog/does-neutrogena-have-microplastics" className="text-forest underline">does Neutrogena have microplastics</Link>.
      </p>

      <h2>What this means for a rinse-off versus a leave-on</h2>
      <p>
        A cleanser is on your face for thirty seconds and then goes down the
        drain. A moisturiser stays on for hours. For the environmental case, the
        cleanser is worse in that respect — everything in it reaches the water system.
        For the personal-exposure case, the leave-on matters more.
      </p>
      <p>
        Most of the Hydro Boost hits are cleansers, which softens the personal
        exposure story and sharpens the environmental one. If your concern is
        what goes down the drain, the gel cleansers are the ones to change. If
        your concern is what sits on your skin all day, focus on the Water Gel
        moisturiser and the eye cream.
      </p>

      <BlogAppCTA
        headline="Audit your own bathroom shelf"
        sub="Scan any moisturiser, cleanser or SPF with MicroPlastics to see its polymers, a 0–100 score, and a cleaner product in the same category."
      />

      <h2>The comparison that matters: skincare versus makeup</h2>
      <p>
        In July we ran this same method across{' '}
        <Link href="/blog/microplastics-in-makeup-brands-audited" className="text-forest underline">81 prestige makeup formulas from Fenty, Huda, rhode and Kylie</Link>{' '}
        and found 24 containing solid polymer particles — around 30%. Drugstore
        skincare as a whole comes in at 16%, and the two gentle-positioned
        brands at 6%.
      </p>
      <p>
        So the intuition that the dermatologist aisle is cleaner is{' '}
        <strong>correct</strong>, and by a wide margin. This is worth saying
        plainly, because the reflex in this space is to assume every large brand
        is equally bad. On this measure they are not. CeraVe and Cetaphil are
        doing something genuinely different from a colour cosmetics house, and it
        shows up in the data.
      </p>

      <h2>What to actually do</h2>
      <ul>
        <li>
          <strong>Your basic moisturiser is almost certainly fine.</strong>{' '}
          CeraVe Moisturizing Cream, Daily Moisturizing Lotion and the classic
          Cetaphil lotion and cleanser are clean.
        </li>
        <li>
          <strong>Check your SPF first.</strong> Across all three brands,
          sunscreens and SPF moisturisers are where polymer particles cluster.
        </li>
        <li>
          <strong>If you use Hydro Boost, that is the line to look at.</strong>{' '}
          Fifteen products, and the cleansers rinse straight into the water
          system.
        </li>
        <li>
          <strong>Read for five words.</strong> Nylon, polyethylene,
          acrylates, methacrylate, crosspolymer. That covers nearly every hit in
          this audit.
        </li>
        <li>
          <strong>Do not panic about dimethicone.</strong> It is a synthetic
          polymer and it is not a microplastic. Different problem, different
          conversation.
        </li>
      </ul>

      <p>
        Brand-level detail:{' '}
        <Link href="/blog/does-cerave-have-microplastics" className="text-forest underline">CeraVe</Link>{' '}
        and{' '}
        <Link href="/blog/does-cetaphil-have-microplastics" className="text-forest underline">Cetaphil</Link>{' '}
        and{' '}
        <Link href="/blog/does-neutrogena-have-microplastics" className="text-forest underline">Neutrogena</Link>.
        For the makeup comparison see{' '}
        <Link href="/blog/microplastics-in-makeup-brands-audited" className="text-forest underline">the July makeup audit</Link>{' '}
        and{' '}
        <Link href="/blog/clean-beauty-microplastics-loophole" className="text-forest underline">clean beauty&apos;s plastic loophole</Link>.
        To check anything yourself,{' '}
        <Link href="/blog/how-to-check-cosmetic-ingredients-for-microplastics-2026" className="text-forest underline">how to read a cosmetic ingredient list</Link>.
      </p>

      <BlogFAQ
        items={[
          {
            question: 'Which drugstore skincare brand has the fewest microplastics?',
            answer: 'CeraVe and Cetaphil are level, at 4 of 71 and 5 of 79 published formulas respectively — about 6% each. Neutrogena is substantially higher at 38 of 140, or 27%. Split by category that is 18 of 72 skincare (25%), 20 of 54 makeup (37%) and 0 of 14 haircare, so its skincare alone still runs four times the other two. All three are cleaner than the prestige makeup brands we audited in July, which ran near 30%.',
          },
          {
            question: 'Does CeraVe have microplastics?',
            answer: 'Four of the 71 CeraVe formulas that publish an ingredient list contain a solid synthetic polymer particle: the Makeup Removing Cleanser Balm (polyethylene), the Soothing Body Wash for Very Dry Skin (silica dimethyl silylate), the Skin Renewing Retinol Serum (vinyl dimethicone crosspolymer) and the Ultra-Light Moisturizing Lotion SPF 30 (methyl methacrylate crosspolymer). The core ceramide moisturisers that most people buy contain none.',
          },
          {
            question: 'Why is Neutrogena Hydro Boost so high?',
            answer: 'Fifteen Hydro Boost products contain a solid synthetic polymer, which is nearly 40% of every hit in the brand. The reason is textural: the weightless water-gel feel the line is sold on is produced by acrylates copolymer and dimethicone crosspolymer networks that hold water and break under shear. The polymer is not an additive to the formula so much as the thing that creates the sensation the product is marketed for.',
          },
          {
            question: 'Is dimethicone a microplastic?',
            answer: 'No. Dimethicone is a silicone — a synthetic polymer, but a liquid one that is not supplied as discrete solid particles, so it falls outside the EU definition of synthetic polymer microparticles. The same applies to cyclopentasiloxane, the PEGs and polyquaternium. They raise separate questions about biodegradability, but counting them as microplastics inflates the numbers and makes audits impossible to compare.',
          },
          {
            question: 'Are microplastics in skincare banned?',
            answer: 'Not yet, and not for a long time. Rinse-off products with deliberately added microbeads were restricted first, but leave-on cosmetics containing synthetic polymer microparticles have until October 2035 under the EU restriction, with staged deadlines before then. In the US there is no federal restriction beyond the 2015 Microbead-Free Waters Act, which covers rinse-off exfoliants only.',
          },
        ]}
      />

      <BlogSources
        items={[
          { authors: 'MicroPlastics Research Desk', year: 2026, title: 'First-party INCI audit of 290 CeraVe, Cetaphil and Neutrogena formulas (product sitemaps walked and ingredient lists extracted from brand sites, August 2026)', publisher: 'MicroPlastics' },
          { authors: 'L’Oréal (CeraVe)', year: 2026, title: 'CeraVe product ingredient listings', publisher: 'cerave.com', url: 'https://www.cerave.com/' },
          { authors: 'Galderma (Cetaphil)', year: 2026, title: 'Cetaphil product ingredient listings', publisher: 'cetaphil.com', url: 'https://www.cetaphil.com/' },
          { authors: 'Kenvue (Neutrogena)', year: 2026, title: 'Neutrogena product ingredient listings', publisher: 'neutrogena.com', url: 'https://www.neutrogena.com/' },
          { authors: 'European Commission', year: 2023, title: 'Commission Regulation (EU) 2023/2055 restricting synthetic polymer microparticles (REACH Annex XVII entry 78)', publisher: 'Official Journal of the European Union', url: 'https://eur-lex.europa.eu/eli/reg/2023/2055/oj' },
        ]}
      />
    </BlogPostLayout>
  )
}
